import { AbsolutePath, URLString } from '@/types/navigation';

import { complement, curry, either, equals, isEmpty, joinWithSlash } from '../common';
import { urlWithProtocolRegExp } from '../regExp';

type PathSegment = string;

const isAbsolute = (path: string): path is AbsolutePath => path.startsWith('/');

const isRelativePath = (path: string) => {
  return !urlWithProtocolRegExp.test(path) && (path.includes('./') || path.includes('../')) && !path.startsWith('/');
};

const updateLastPathSegment = (basePath: string, newSegment: string): string => {
  const segments = basePath.split('/').filter(Boolean);
  const newParts = newSegment.split('/').filter(Boolean);

  const updatedSegments = !isEmpty(segments) ? segments.slice(0, -1) : [];

  return joinWithSlash([...updatedSegments, ...newParts]);
};

export const toAbsolutePath = (path: string): AbsolutePath => (isAbsolute(path) ? path : `/${path}`);

const isURL = (path: string): path is URLString => /^https?:\/\//.test(path);

const isMailOuterLink = (path: string) => path.startsWith('mailto');

export const isExternalLink = either(isURL, isMailOuterLink);
const isTwoDots = equals('..');
const isDot = equals('.');
export const isDotOrTwoDots = either(isTwoDots, isDot);

export const splitPath = (path: PathSegment): PathSegment[] => path.split('/').filter(Boolean);

export const countBackSteps = (segments: PathSegment[]) => segments.filter(isTwoDots).length;

export const processRelativeSegments = (segments: PathSegment[]) => ({
  backSteps: countBackSteps(segments),
  filtered: segments.filter(complement(isDotOrTwoDots)),
});

export const applyBackSteps = (current: PathSegment[], steps: number) => {
  return current.slice(0, Math.max(current.length - steps - 1, 0));
};

export const resolvePath = curry<[AbsolutePath, string], AbsolutePath>((currentPath, relativePath) => {
  if (relativePath.startsWith('/')) {
    return toAbsolutePath(`${joinWithSlash(splitPath(relativePath))}`);
  }

  if (relativePath.startsWith('#')) {
    return `${toAbsolutePath(currentPath)}${relativePath}`;
  }

  if (!isRelativePath(relativePath)) {
    return toAbsolutePath(updateLastPathSegment(currentPath, relativePath));
  }

  const currentSegments = splitPath(currentPath);
  const { backSteps, filtered } = processRelativeSegments(splitPath(relativePath));
  return [...applyBackSteps(currentSegments, backSteps), ...filtered].reduce<AbsolutePath>(
    (acc, seg) => (equals(acc, '/') ? toAbsolutePath(seg) : toAbsolutePath(`${acc}/${seg}`)),
    '/',
  );
});

export const addQueryParamToString = (uri: string, key: string, value: string) => {
  const isUrlParamRegExp = new RegExp('([?&])' + key + '=.*?(&|$)', 'i');
  const separator = uri.indexOf('?') !== -1 ? '&' : '?';
  if (uri.match(isUrlParamRegExp)) {
    return uri.replace(isUrlParamRegExp, '$1' + key + '=' + value + '$2');
  }
  return uri + separator + key + '=' + value;
};
